import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, FileText, Phone, Mail, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import api, { resolveMedia } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import { Reveal } from "@/components/motion/Reveal";

const eur = (n) => n == null ? null : new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—";

const STEPS = [
  { n: "01", title: "Verifica dello Studio", text: "Giovanna esamina personalmente i dati che hai inserito: superficie, esigenze e contesto dell'immobile." },
  { n: "02", title: "Contatto personale", text: "Vieni ricontattata/o per un confronto diretto: dettagli, eventuali sopralluoghi e risposta alle tue domande." },
  { n: "03", title: "Preventivo definitivo", text: "Solo dopo la verifica ricevi il preventivo professionale completo, con importi e tempistiche confermati." },
];

export default function StimaConferma() {
  useSEO({ title: "Conferma stima" });
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [err, setErr] = useState(false);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/estimator/track/${token}`).then((r) => setData(r.data)).catch(() => setErr(true));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [token]);

  const askConfirmation = async () => {
    setBusy(true);
    try {
      await api.post(`/estimator/track/${token}/request-confirmation`);
      setSent(true);
      toast.success("Richiesta di conferma registrata");
    } catch {
      toast.error("Errore, riprova");
    } finally { setBusy(false); }
  };

  if (err) return <div className="container-x py-24 text-taupe" data-testid="stima-track-error">Link non valido o richiesta non trovata.</div>;
  if (!data) return <div className="container-x py-24 text-taupe">Caricamento…</div>;

  const confirmed = sent || data.status === "da verificare" || data.status === "confermata";

  return (
    <div className="container-x py-16 max-w-3xl" data-testid="page-stima-conferma">
      <Reveal>
        <span className="eyebrow">Stima preliminare personalizzata</span>
        <h1 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
          La tua stima <em className="text-gold font-light">{data.number}</em>
        </h1>
        <p className="mt-5 text-taupe leading-relaxed font-light max-w-xl">
          Grazie per la fiducia. Questa è la tua stima preliminare: un punto di partenza
          elaborato sulle tariffe reali dello Studio, non un preventivo vincolante.
        </p>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-10 bg-white border border-hair p-6 md:p-8" data-testid="stima-track-summary">
          <div className="flex justify-between border-b border-hair/60 pb-3"><span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Servizio</span><span className="text-sm">{data.package}</span></div>
          <div className="flex justify-between border-b border-hair/60 py-3"><span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Data</span><span className="text-sm">{fmtDate(data.created_at)}</span></div>
          <div className="flex justify-between border-b border-hair/60 py-3 items-center">
            <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Totale indicativo</span>
            <span className="font-serif text-2xl">{data.manual ? "Su misura" : eur(data.total)}</span>
          </div>
          <div className="flex justify-between pt-3 items-center">
            <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Stato</span>
            <span className="uppercase text-xs tracking-[0.12em] text-gold" data-testid="stima-track-status">{data.status}</span>
          </div>
          {data.manual && (
            <p className="mt-4 text-xs text-taupe leading-relaxed border-t border-hair/60 pt-4">
              La tua richiesta riguarda una superficie o una trasferta oltre le fasce standard:
              lo Studio la valuterà personalmente e ti invierà una proposta su misura.
            </p>
          )}
        </div>
      </Reveal>

      <Reveal delay={0.14}>
        <div className="mt-6 flex flex-wrap gap-3">
          {data.pdf_url && (
            <a href={resolveMedia(data.pdf_url)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-5 py-3 min-h-[48px] hover:bg-ink hover:text-bone transition-colors"
              data-testid="stima-track-pdf">
              <FileText size={15} /> Apri la tua stima (PDF)
            </a>
          )}
          <Link to="/prenota"
            className="inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border border-gold text-gold px-5 py-3 min-h-[48px] hover:bg-gold hover:text-ink transition-colors"
            data-testid="stima-track-prenota">
            <CalendarCheck size={15} /> Prenota una chiamata
          </Link>
        </div>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="mt-14">
          <span className="eyebrow text-gold">Cosa succede ora</span>
          <div className="mt-6 grid sm:grid-cols-3 gap-px bg-hair border border-hair" data-testid="stima-track-steps">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white p-6" data-testid={`stima-track-step-${s.n}`}>
                <div className="font-serif text-3xl text-gold">{s.n}</div>
                <h3 className="font-serif text-lg mt-3">{s.title}</h3>
                <p className="text-sm text-taupe mt-2 leading-relaxed font-light">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={0.26}>
        {confirmed ? (
          <div className="mt-10 bg-white border border-gold/50 p-6 flex gap-4 items-start" data-testid="stima-track-done">
            <Check size={18} className="text-gold shrink-0 mt-0.5" />
            <p className="text-sm text-taupe leading-relaxed">
              Richiesta registrata. Lo Studio verificherà personalmente i dati e ti ricontatterà
              per la conferma definitiva. Non devi fare altro: ti scriviamo noi.
            </p>
          </div>
        ) : (
          <div className="mt-10 bg-white border border-hair p-6 md:p-8" data-testid="stima-track-cta">
            <h2 className="font-serif text-2xl">Vuoi procedere?</h2>
            <p className="text-sm text-taupe mt-2 leading-relaxed font-light">
              Con un click segnali allo Studio il tuo interesse: la verifica dei dati partirà subito
              e sarai ricontattata/o personalmente. Nessun pagamento è richiesto in questa fase.
            </p>
            <button onClick={askConfirmation} disabled={busy}
              className="btn-ink mt-6 w-full sm:w-auto min-h-[48px] disabled:opacity-50"
              data-testid="stima-track-confirm">
              {busy ? "Invio…" : "Richiedi la conferma dello Studio"}
            </button>
          </div>
        )}
      </Reveal>

      {settings && (settings.phone || settings.email) && (
        <Reveal delay={0.3}>
          <div className="mt-10 border-t border-hair pt-8" data-testid="stima-track-contacts">
            <p className="text-[10px] tracking-[0.14em] uppercase text-taupe">Hai dubbi nel frattempo?</p>
            <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              {settings.phone && (
                <a href={`tel:${settings.phone}`} className="inline-flex items-center gap-2 text-ink hover:text-gold transition-colors" data-testid="stima-track-phone">
                  <Phone size={14} className="text-gold" /> {settings.phone}
                </a>
              )}
              {settings.email && (
                <a href={`mailto:${settings.email}`} className="inline-flex items-center gap-2 text-ink hover:text-gold transition-colors" data-testid="stima-track-email">
                  <Mail size={14} className="text-gold" /> {settings.email}
                </a>
              )}
            </div>
          </div>
        </Reveal>
      )}
    </div>
  );
}
